import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const NAV_COPY = {
  en: {
    features: 'Features',
    philosophy: 'Why',
    about: 'About',
    blog: 'Blog',
    reviews: 'Reviews',
    faq: 'FAQ',
    contact: 'Contact',
    login: 'Log in',
    signup: 'Start free',
  },
  ru: {
    features: 'Возможности',
    philosophy: 'Зачем',
    about: 'О нас',
    blog: 'Блог',
    reviews: 'Отзывы',
    faq: 'FAQ',
    contact: 'Контакты',
    login: 'Войти',
    signup: 'Попробовать',
  },
};

// Same shrink-on-scroll-down / expand-on-scroll-up behavior as the landing
// page's own nav (see Landing.jsx's original useScrolled).
function useScrolled(threshold = 60) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    const UPWARD_JITTER = 6;
    const onScroll = () => {
      const y = window.scrollY;
      if (y <= threshold) setScrolled(false);
      else if (y > lastY) setScrolled(true);
      else if (y < lastY - UPWARD_JITTER) setScrolled(false);
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

// Shared by Landing.jsx and the public blog pages so the top nav looks and
// behaves identically everywhere. On non-home pages the section links point
// back to `/#section` (plain <a>, full navigation) since those sections only
// exist on the landing page itself.
export default function LandingNav({ lang, toggleLang, home = false }) {
  const navigate = useNavigate();
  const scrolled = useScrolled(60);
  const t = NAV_COPY[lang];
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 900) closeMenu(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sectionHref = (anchor) => (home ? `#${anchor}` : `/#${anchor}`);

  return (
    <>
      <nav className={`landing-nav${scrolled ? ' is-shrunk' : ''}`}>
        {home ? (
          <a href="#top" className="landing-nav-logo" aria-label="NOVAS home">NOVAS</a>
        ) : (
          <Link to="/" className="landing-nav-logo" aria-label="NOVAS home">NOVAS</Link>
        )}
        <ul className="landing-nav-links">
          <li><a href={sectionHref('philosophy')}>{t.philosophy}</a></li>
          <li><a href={sectionHref('features')}>{t.features}</a></li>
          <li><a href={sectionHref('about')}>{t.about}</a></li>
          <li><Link to="/blog">{t.blog}</Link></li>
          <li><a href={sectionHref('reviews')}>{t.reviews}</a></li>
          <li><a href={sectionHref('faq')}>{t.faq}</a></li>
          <li><a href={sectionHref('contact')}>{t.contact}</a></li>
        </ul>
        <div className="landing-nav-controls">
          <button className="landing-nav-cta-ghost" onClick={() => navigate('/login')}>
            {t.login}
          </button>
          <button className="landing-nav-cta" onClick={() => navigate('/register')}>
            {t.signup}
          </button>
          <button
            type="button"
            className={`landing-nav-burger${menuOpen ? ' is-open' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="landing-mobile-menu"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </nav>

      <button
        type="button"
        className="landing-lang-orb"
        onClick={toggleLang}
        aria-label="Toggle language"
      >
        {lang === 'en' ? 'RU' : 'EN'}
      </button>

      <div
        className={`landing-mobile-menu-backdrop${menuOpen ? ' is-open' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
      <div
        id="landing-mobile-menu"
        className={`landing-mobile-menu${menuOpen ? ' is-open' : ''}`}
      >
        <ul className="landing-mobile-menu-links">
          <li><a href={sectionHref('philosophy')} onClick={closeMenu}>{t.philosophy}</a></li>
          <li><a href={sectionHref('features')} onClick={closeMenu}>{t.features}</a></li>
          <li><a href={sectionHref('about')} onClick={closeMenu}>{t.about}</a></li>
          <li><Link to="/blog" onClick={closeMenu}>{t.blog}</Link></li>
          <li><a href={sectionHref('reviews')} onClick={closeMenu}>{t.reviews}</a></li>
          <li><a href={sectionHref('faq')} onClick={closeMenu}>{t.faq}</a></li>
          <li><a href={sectionHref('contact')} onClick={closeMenu}>{t.contact}</a></li>
        </ul>
        <button
          type="button"
          className="landing-mobile-menu-login"
          onClick={() => { closeMenu(); navigate('/login'); }}
        >
          {t.login}
        </button>
      </div>
    </>
  );
}
